import React, { useState, useEffect, useRef } from 'react';
import './Request.css'; 
import Navbar from '../Navbar/Navbar.js';
import apiClient from '../../api/apiClient.js';
import { useTheme } from '../ThemeContext';  // Import useTheme hook
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';


const Request = () => {
  const { user } = useAuth();
  const { servers: cachedServers } = useData();
  const [url, setUrl] = useState('');
  const [message, setMessage] = useState('');
  const [details, setDetails] = useState('');
  const [email, setEmail] = useState(''); 
  const [banner, setBanner] = useState(null);
  const [servers, setServers] = useState([]);
  const [filteredServers, setFilteredServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState('');
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const { theme } = useTheme();  // Get the current theme

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  // Keep these in sync with the backend's allow-list (Routes/requestRoutes.js).
  const ALLOWED_EXT = ['png', 'jpg', 'jpeg', 'pdf', 'txt'];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  useEffect(() => {
    if (!user) return;

    // Prefer explicit email field; fall back to known MSAL shapes
    if (user.email) {
      setEmail(user.email);
    } else if (user.username) {
      setEmail(user.username);
    } else if (user.mail) {
      setEmail(user.mail);
    }
  }, [user]);

  useEffect(() => {
    setServers(cachedServers || []);
    setFilteredServers(cachedServers || []);
  }, [cachedServers]);

  useEffect(() => {
    if (!banner?.message) return undefined;
    const timer = setTimeout(() => setBanner(null), 4500);
    return () => clearTimeout(timer);
  }, [banner]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsDropdownVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const input = e.target.value;
    setUrl(input);
    setIsDropdownVisible(true);
    // Filter servers based on the input
    const filtered = servers.filter((server) =>
      server.link.toLowerCase().includes(input.toLowerCase())
    );
    setFilteredServers(filtered);
  };

  const handleSelectServer = (serverLink) => {
    setUrl(serverLink);
    setSelectedServer(serverLink);
    setFilteredServers(servers); // Reset filtered servers to the full list
    setIsDropdownVisible(false);
  };

  const handleInputFocus = () => {
    setFilteredServers(servers);
    setIsDropdownVisible(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setAttachment(null);
      return;
    }
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      setBanner({ type: 'error', message: 'Unsupported file type. Allowed: PNG, JPG, PDF, TXT.' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      setAttachment(null);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setBanner({ type: 'error', message: 'File is too large. Maximum size is 5MB.' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      setAttachment(null);
      return;
    }
    setAttachment(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBanner(null);

    if (!selectedServer || !message || !email) {
      setBanner({ type: 'error', message: 'Please select a valid server and fill in all required fields.' });
      return;
    }

    setIsSubmitting(true);
    const requestData = new FormData();
    requestData.append('url', selectedServer);
    requestData.append('message', message);
    requestData.append('details', details);
    requestData.append('email', email);
    requestData.append('organization', user?.organization || 'default');
    if (attachment) {
      requestData.append('attachment', attachment);
    }

    try {
      const response = await apiClient.post('/api/requests', requestData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      if (response.status === 200) {
        setBanner({ type: 'success', message: 'Your request has been submitted successfully.' });
        setUrl('');
        setSelectedServer('');
        setMessage('');
        setDetails('');
        setAttachment(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    } catch (error) {
      setBanner({ type: 'error', message: 'There was an error submitting your request. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`request-container ${theme}`}>  {/* Use the theme class */}
      <Navbar />
      <div className="request-content page-transition">
        <div className="page-shell request-page">
          <div className="request-header">
            <h1 className="page-title">Request</h1>
            <p className="request-subtitle">Tell us what’s wrong and we’ll take it from there.</p>
          </div>
          <div className="toast-stack" aria-live="polite" aria-atomic="true">
            {banner?.message && (
              <div className={`toast ${banner.type}`}>
                <div className="toast-content">
                  <span className="toast-icon" aria-hidden />
                  <div className="toast-text">
                    <div className="toast-title">{banner.type === 'success' ? 'All good' : 'Please check'}</div>
                    <div>{banner.message}</div>
                  </div>
                </div>
                <button
                  type="button"
                  className="toast-dismiss"
                  aria-label="Dismiss message"
                  onClick={() => setBanner(null)}
                >
                  ×
                </button>
              </div>
            )}
          </div>
          <form className="request-form" onSubmit={handleSubmit}>
            <label>
              Your Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                disabled={!!user}
                required
              />
            </label>
            <label>
              URL with Issue
              <div className="dropdown-container">
                <input
                  type="text"
                  placeholder="Type to filter servers..."
                  value={url}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  ref={inputRef}
                  className="input-field"
                  required
                />
                {isDropdownVisible && filteredServers.length > 0 && (
                  <ul className="dropdown-list" ref={dropdownRef}>
                    {filteredServers.map((server) => (
                      <li
                        key={server._id}
                        onClick={() => handleSelectServer(server.link)}
                        className="dropdown-item"
                      >
                        {server.link}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </label>
            <label>
              Your Message
              <textarea
                placeholder="Describe the issue you're facing..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="input-field"
                rows="4"
                required
              />
            </label>
            <label>
              Additional Details
              <textarea
                placeholder="Any additional details..."
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                className="input-field"
                rows="3"
              />
            </label>

            <label>
              Attachment (optional)
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".png,.jpg,.jpeg,.pdf,.txt,image/png,image/jpeg,application/pdf,text/plain"
                className="input-field file-field"
              />
              <span className="field-hint">PNG, JPG, PDF or TXT · up to 5MB</span>
            </label>

            <div className="submit-row">
              <button className="submit-button" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Request;
